import { useEffect, useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  SimpleGrid,
  Grid,
  GridItem,
  List,
  ListItem,
  Divider,
  Tag,
  Wrap,
  WrapItem,
  useColorMode,
  Icon,
} from "@chakra-ui/react";
import {
  Layers,
  MessageSquare,
  Users,
  Compass,
} from "lucide-react";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { ContactForm } from "../components/ContactForm";
import { getColor } from "../brandConfig";

const Home = () => {
  const { colorMode } = useColorMode();
  const isLight = colorMode === "light";

  // Palette helpers
  const bg = getColor("background.main", colorMode);
  const bgAlt = getColor("background.light", colorMode);
  const cardBg = getColor("background.card", colorMode);
  const textPrimary = getColor(isLight ? "text.primary" : "text.primaryDark", colorMode);
  const textSoft = getColor(isLight ? "text.secondary" : "text.secondaryDark", colorMode);
  const accent = getColor("primary", colorMode);
  const accentHover = getColor("primaryHover", colorMode);
  const rule = getColor("border.medium", colorMode);
  const inkDark = isLight ? "#18140f" : "#08090a";
  const whiteTone = isLight ? "#fdfaf5" : "#FFFFFF";
  const accentLight = getColor("secondary", colorMode);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  // Nav links from other pages arrive as /#section
  useEffect(() => {
    const anchor = window.location.hash.slice(1);
    if (anchor) setTimeout(() => scrollTo(anchor), 100);
  }, []);

  // Every "Book a Conversation" CTA lands on the contact form, optionally preselecting a service
  const [selectedService, setSelectedService] = useState<string | undefined>();
  const bookConversation = (service?: string) => {
    if (service) setSelectedService(service);
    scrollTo("contact");
  };

  // ── Why clients bring me in
  const situations = [
    "A transformation.",
    "A technology implementation.",
    "A strategic initiative.",
    "A period of organisational uncertainty.",
    "A challenge where the path forward isn't entirely clear.",
  ];

  const strengths = [
    "Systems thinking",
    "Strategic communication",
    "Change expertise",
    "Organisational insight",
    "Behavioural understanding",
    "Practical implementation experience",
  ];

  // ── Services data
  const services = [
    {
      num: "01",
      icon: Layers,
      title: "Change, Readiness & Transformation",
      lead: "Helping organisations prepare for, implement and embed change successfully.",
      desc: "From workforce transformation and technology implementation through to organisational redesign and service improvement, I help leaders reduce uncertainty, strengthen readiness and support adoption.",
      areas: [
        "Change planning and delivery",
        "Readiness assessments",
        "Stakeholder engagement",
        "Change communication",
        "Adoption planning",
        "Reinforcement activities",
        "Hypercare design",
        "Transition to business-as-usual",
      ],
    },
    {
      num: "02",
      icon: MessageSquare,
      title: "Strategic Communication",
      lead: "Communication that helps people understand what is happening, why it matters, and what comes next.",
      desc: "I support leaders and organisations to communicate with clarity, confidence and intent, particularly during moments that carry complexity or consequence.",
      areas: [
        "Leadership communication",
        "Executive messaging",
        "Change communication",
        "Organisational announcements",
        "Stakeholder communication",
        "Narrative development",
        "Communication reviews",
        "Sensitive communications",
      ],
    },
    {
      num: "03",
      icon: Users,
      title: "Employee Experience & Capability",
      lead: "Helping people navigate change with confidence.",
      desc: "I design employee experiences that improve understanding, increase capability and support successful adoption.",
      areas: [
        "Knowledge hub design",
        "SharePoint and intranet content",
        "Employee resources",
        "Learning support",
        "Readiness materials",
        "FAQs and guidance resources",
        "Reinforcement programs",
        "Capability-building resources",
      ],
    },
    {
      num: "04",
      icon: Compass,
      title: "Strategic Advisory",
      lead: "An independent thinking partner for complex organisational challenges.",
      desc: "Sometimes leaders don't need another consultant. They need someone who can think with them. Someone who can connect ideas, challenge assumptions, identify risks and bring structure to ambiguity. That's the role I often play.",
      areas: [] as string[],
    },
  ];

  // ── Process steps
  const steps = [
    {
      num: "01",
      title: "Understand",
      desc: "I take the time to understand your context, stakeholders, challenges and desired outcomes.",
    },
    {
      num: "02",
      title: "Analyse",
      desc: "I identify patterns, opportunities, dependencies and risks to create a clear picture of what is happening and why.",
    },
    {
      num: "03",
      title: "Shape",
      desc: "Together, we develop practical strategies, communications, roadmaps or solutions that align with your objectives.",
    },
    {
      num: "04",
      title: "Embed",
      desc: "I help translate plans into action through communication, readiness, capability building and implementation support.",
    },
  ];

  // ── About
  const descriptors = [
    "Strategic",
    "Calm under pressure",
    "Practical",
    "Insightful",
    "Collaborative",
    "Trusted",
    "Human-centred",
    "Genuine",
  ];

  const initiatives = [
    "Technology implementation",
    "Workforce transformation",
    "Organisational change",
    "Customer experience improvement",
    "Strategic communication",
    "Readiness and adoption",
  ];

  // ── Recent engagements
  const engagements = [
    {
      title: "Workforce Transformation & Technology Implementation",
      desc: "Supported the implementation of a workforce management platform across a multi-site workforce. Led communication, readiness, training coordination, knowledge hub development and hypercare planning. Helped leaders and employees transition confidently to new ways of working.",
    },
    {
      title: "Strategic Communication Reviews",
      desc: "Partnered with leaders and organisations to strengthen executive communications, improve narrative clarity and transform complex technical information into clear and actionable messaging.",
    },
    {
      title: "Change Readiness & Adoption",
      desc: "Designed and delivered readiness activities, stakeholder engagement approaches and adoption support strategies to help employees successfully transition through change.",
    },
  ];

  // ── What it's like to work with me
  const themes = [
    "Calm under pressure",
    "Trusted advice",
    "Practical support",
    "Genuine care",
    "Strong judgement",
    "Collaborative working style",
    "Ability to simplify complexity",
    "Positive energy during challenging projects",
  ];

  const experienceQuotes = [
    {
      quote: "Across multiple complex projects, Luna consistently strengthens the clarity and intent of our work. She builds trust quickly, collaborates seamlessly with teams and clients, and brings thoughtful insight, reliability and positive energy. Her contribution genuinely elevates the outcomes we deliver.",
      role: "Founder & Managing Director",
    },
    {
      quote: "Thank you so much for all your support and patient expertise. You were such a breath of fresh air.",
      role: "Project Team Member",
    },
  ];

  // ── Testimonials
  const testimonials = [
    {
      quote: "Since joining us, Luna provided exceptional support to ensure our teams were prepared and supported throughout this important project. We greatly valued her expertise and partnership.",
      role: "CEO, Public Sector Organisation",
    },
    {
      quote: "Luna has been an awesome part of the team and we would have been lost without her.",
      role: "Project Sponsor",
    },
    {
      quote: "Your knowledge, calmness under pressure and genuine care for getting it right.",
      role: "Senior Project Manager",
    },
    {
      quote: "The team appreciated your knowledge, willingness to help and the way you've always been there to work through challenges with us.",
      role: "Operational Leader",
    },
  ];
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  // ── Six Conditions
  const conditions = [
    {
      num: "01",
      title: "Stability & Clarity",
      lead: "People engage more effectively when expectations are clear and uncertainty is reduced.",
      desc: "I help organisations create communication and change approaches that build understanding and confidence rather than confusion.",
    },
    {
      num: "02",
      title: "Visibility & Recognition",
      lead: "People are more likely to engage when they feel their contribution is recognised and valued.",
      desc: "I help leaders create communication and engagement approaches that strengthen connection, ownership and participation.",
    },
    {
      num: "03",
      title: "Access & Understanding",
      lead: "Access to information, decision-making and support influences how successfully change is adopted.",
      desc: "I help organisations remove barriers and create clarity around what people need to know, do and understand.",
    },
    {
      num: "04",
      title: "Inclusion & Belonging",
      lead: "People experience change differently.",
      desc: "I help organisations design communication and engagement approaches that recognise different perspectives and create space for meaningful participation.",
    },
    {
      num: "05",
      title: "Alignment as Leadership™",
      lead: "People pay attention to what organisations do, not just what they say.",
      desc: "I help leaders align communication, behaviours and organisational priorities to build credibility and trust.",
    },
    {
      num: "06",
      title: "Systems Through Story™",
      lead: "Every organisation has stories that shape perceptions, behaviours and decision-making.",
      desc: "I help leaders understand the patterns, assumptions and narratives influencing how people experience change.",
    },
  ];

  // ── Shared label style
  const labelStyle = {
    fontSize: "0.7rem",
    fontWeight: "500",
    letterSpacing: "0.18em",
    textTransform: "uppercase" as const,
    color: accent,
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
  };

  const primaryButtonProps = {
    size: "lg",
    bg: accent,
    color: whiteTone,
    fontWeight: "500",
    fontSize: "0.85rem",
    letterSpacing: "0.1em",
    textTransform: "uppercase" as const,
    px: 8,
    py: 6,
    borderRadius: "none",
    border: `1.5px solid ${accent}`,
    _hover: { bg: accentHover, borderColor: accentHover },
  };

  const pillProps = {
    size: "md",
    bg: cardBg,
    color: textSoft,
    border: `1px solid ${rule}`,
    borderRadius: "none",
    px: 4,
    py: 2,
    fontSize: "0.78rem",
    letterSpacing: "0.06em",
    cursor: "default",
    _hover: { borderColor: accent, color: accent },
    transition: "border-color 0.2s, color 0.2s",
  };

  const bulletList = (items: string[], color: string, marker: string) => (
    <List spacing={2} pl={0}>
      {items.map((item) => (
        <ListItem key={item} display="flex" alignItems="center" gap={3} color={color} fontSize="0.92rem">
          <Box as="span" display="inline-block" w="0.9rem" h="1px" bg={marker} flexShrink={0} />
          {item}
        </ListItem>
      ))}
    </List>
  );

  return (
    <Box bg={bg} minH="100vh">
      <NavbarWithCallToAction />

      {/* ─── HERO ──────────────────────────────────────────────── */}
      <Box
        id="hero"
        as="section"
        bg={inkDark}
        color={whiteTone}
        minH={{ base: "90vh", md: "100vh" }}
        display="flex"
        alignItems="center"
        position="relative"
        overflow="hidden"
        pt={{ base: "6rem", md: "9rem" }}
        pb={{ base: "4rem", md: "6rem" }}
        px={{ base: "1.5rem", md: "5vw" }}
      >
        {/* Decorative vertical line */}
        <Box
          display={{ base: "none", lg: "block" }}
          position="absolute"
          top={0}
          right="18vw"
          width="1px"
          height="100%"
          bgGradient={`linear(to-b, transparent, ${accentLight}60 30%, ${accentLight}60 70%, transparent)`}
        />

        {/* Abstract hero graphic: clarity/connection motif */}
        <Box
          display={{ base: "none", lg: "block" }}
          position="absolute"
          top="50%"
          right="3vw"
          transform="translateY(-50%)"
          w="15vw"
          h="15vw"
          maxW="220px"
          maxH="220px"
          opacity={0.18}
          pointerEvents="none"
        >
          <svg viewBox="0 0 220 220" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
            {/* Outer thin ring */}
            <circle cx="110" cy="110" r="105" stroke={accentLight} strokeWidth="0.75" />
            {/* Mid ring */}
            <circle cx="110" cy="110" r="78" stroke={accentLight} strokeWidth="0.5" />
            {/* Inner ring */}
            <circle cx="110" cy="110" r="48" stroke={accentLight} strokeWidth="0.5" />
            {/* Cross-hairs */}
            <line x1="110" y1="5" x2="110" y2="215" stroke={accentLight} strokeWidth="0.5" />
            <line x1="5" y1="110" x2="215" y2="110" stroke={accentLight} strokeWidth="0.5" />
            {/* Diagonal accents */}
            <line x1="36" y1="36" x2="184" y2="184" stroke={accentLight} strokeWidth="0.4" />
            <line x1="184" y1="36" x2="36" y2="184" stroke={accentLight} strokeWidth="0.4" />
            {/* Centre dot */}
            <circle cx="110" cy="110" r="3" fill={accentLight} />
            {/* Corner marks */}
            <rect x="4" y="4" width="16" height="1" fill={accentLight} />
            <rect x="4" y="4" width="1" height="16" fill={accentLight} />
            <rect x="200" y="4" width="16" height="1" fill={accentLight} />
            <rect x="215" y="4" width="1" height="16" fill={accentLight} />
            <rect x="4" y="215" width="16" height="1" fill={accentLight} />
            <rect x="4" y="200" width="1" height="16" fill={accentLight} />
            <rect x="200" y="215" width="16" height="1" fill={accentLight} />
            <rect x="215" y="200" width="1" height="16" fill={accentLight} />
          </svg>
        </Box>

        {/* Dot-grid texture, far right edge */}
        <Box
          display={{ base: "none", xl: "block" }}
          position="absolute"
          bottom="6rem"
          right="2vw"
          w="120px"
          h="120px"
          opacity={0.12}
          pointerEvents="none"
          style={{
            backgroundImage: `radial-gradient(circle, ${accentLight} 1px, transparent 1px)`,
            backgroundSize: "12px 12px",
          }}
        />

        <Container maxW="container.xl" px={0}>
          <VStack align="flex-start" spacing={0} maxW="720px">
            <HStack mb={10} spacing={4}>
              <Box w="3rem" h="1px" bg={accentLight} />
              <Text fontSize="0.72rem" fontWeight="400" letterSpacing="0.2em" textTransform="uppercase" color={accentLight}>
                L. James Solutions
              </Text>
            </HStack>

            <Heading
              as="h1"
              fontFamily="Georgia, 'Times New Roman', serif"
              fontWeight="300"
              fontSize={{ base: "3rem", md: "5rem", lg: "6.5rem" }}
              lineHeight="1.08"
              letterSpacing="-0.02em"
              color={whiteTone}
              mb={8}
            >
              Turning complexity{" "}
              <Box as="em" fontStyle="italic" color={accentLight}>
                into clarity
              </Box>
            </Heading>

            <Text
              fontSize={{ base: "0.8rem", md: "0.9rem" }}
              fontWeight="500"
              letterSpacing="0.16em"
              textTransform="uppercase"
              color={accentLight}
              mb={8}
            >
              Strategic Change &amp; Organisational Transformation
            </Text>

            <Text
              fontSize={{ base: "1.05rem", md: "1.2rem" }}
              color="rgba(253,250,245,0.85)"
              lineHeight="1.7"
              fontWeight="300"
              maxW="50ch"
              mb={4}
            >
              I help organisations navigate complexity, align people around what matters, and deliver change that sticks.
            </Text>

            <Text
              fontSize={{ base: "1rem", md: "1.05rem" }}
              color="rgba(253,250,245,0.6)"
              lineHeight="1.75"
              fontWeight="300"
              maxW="58ch"
              mb={12}
            >
              Whether you're implementing new technology, leading organisational change, developing a roadmap, redesigning services, or trying to align stakeholders around a shared direction, I help create the clarity, confidence and momentum needed to move forward.
            </Text>

            <Button {...primaryButtonProps} onClick={() => bookConversation()}>
              Book a Conversation
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* ─── WHY CLIENTS BRING ME IN ────────────────────────────── */}
      <Box id="why" as="section" bg={bg} py={{ base: "5rem", md: "8rem" }} px={{ base: "1.5rem", md: "5vw" }}>
        <Container maxW="container.xl" px={0}>
          <Grid templateColumns={{ base: "1fr", md: "1fr 1.2fr" }} gap={{ base: 12, md: 20 }}>
            <GridItem>
              <Heading
                fontFamily="Georgia, 'Times New Roman', serif"
                fontWeight="300"
                fontSize={{ base: "2rem", md: "3rem" }}
                color={textPrimary}
                lineHeight="1.15"
                mt={4}
                mb={8}
              >
                Why clients bring me in
              </Heading>
              <Text color={textSoft} lineHeight="1.75" mb={4}>
                Clients rarely engage me because they need another communication plan, roadmap, workshop or presentation.
              </Text>
              <Text color={textPrimary} lineHeight="1.6" fontFamily="Georgia, 'Times New Roman', serif" fontStyle="italic" fontSize="1.2rem">
                They bring me in because they're navigating something important.
              </Text>
            </GridItem>

            <GridItem>
              <VStack align="stretch" spacing={6} pt={{ base: 0, md: 4 }}>
                <VStack align="stretch" spacing={0}>
                  {situations.map((s) => (
                    <Box
                      key={s}
                      py={3}
                      pl={4}
                      borderLeft={`1.5px solid ${accent}`}
                      borderBottom={`1px solid ${rule}`}
                      fontFamily="Georgia, 'Times New Roman', serif"
                      fontSize="1.05rem"
                      color={textPrimary}
                    >
                      {s}
                    </Box>
                  ))}
                </VStack>

                <Text color={textSoft} lineHeight="1.75">
                  They need someone who can quickly understand the landscape, connect the dots, challenge assumptions, and bring clarity to what is really happening.
                </Text>
                <Text color={textPrimary} lineHeight="1.75" fontWeight="500">
                  That's where I add value.
                </Text>

                <Box>
                  <Text color={textSoft} lineHeight="1.75" mb={4}>
                    I combine:
                  </Text>
                  <Wrap spacing={2} mb={4}>
                    {strengths.map((p) => (
                      <WrapItem key={p}>
                        <Tag {...pillProps}>{p}</Tag>
                      </WrapItem>
                    ))}
                  </Wrap>
                  <Text color={textSoft} lineHeight="1.75">
                    to help organisations move from complexity to clarity.
                  </Text>
                </Box>
              </VStack>
            </GridItem>
          </Grid>
        </Container>
      </Box>

      {/* ─── COMPLEXITY & THE SYSTEM ────────────────────────────── */}
      <Box id="complexity" as="section" bg={bgAlt} py={{ base: "5rem", md: "8rem" }} px={{ base: "1.5rem", md: "5vw" }}>
        <Container maxW="container.xl" px={0}>
          <Grid templateColumns={{ base: "1fr", md: "1fr 1.3fr" }} gap={{ base: 12, md: 20 }}>
            <GridItem>
              <Heading
                fontFamily="Georgia, 'Times New Roman', serif"
                fontWeight="300"
                fontSize={{ base: "2rem", md: "2.8rem" }}
                color={textPrimary}
                lineHeight="1.2"
                mt={4}
              >
                Complexity is inevitable.{" "}
                <Box as="em" fontStyle="italic" color={accent}>
                  Confusion doesn't have to be.
                </Box>
              </Heading>
            </GridItem>

            <GridItem>
              <VStack align="flex-start" spacing={4}>
                <Text color={textSoft} lineHeight="1.75">
                  Most organisations don't struggle because they lack capability. They struggle because complexity accumulates.
                </Text>
                <Text color={textSoft} lineHeight="1.75">
                  They're navigating competing priorities, fragmented information, stakeholder expectations, unclear ownership, tight timeframes, different interpretations of the same challenge and increasing pressure to deliver meaningful outcomes.
                </Text>
                <Text color={textSoft} lineHeight="1.75">
                  As complexity grows, communication becomes harder, decisions become slower, and progress begins to stall.
                </Text>
                <Text color={textSoft} lineHeight="1.75">
                  Success depends on more than strategy alone.
                </Text>
                <Text color={textSoft} lineHeight="1.75">
                  My role is to help organisations make sense of complexity. I connect information, identify patterns, bring structure to ambiguity, and help leaders and teams move forward with confidence.
                </Text>
                <Box
                  mt={4}
                  pl={6}
                  borderLeft={`3px solid ${accent}`}
                  bg={cardBg}
                  py={5}
                  pr={6}
                  fontFamily="Georgia, 'Times New Roman', serif"
                  fontSize="1.3rem"
                  fontStyle="italic"
                  fontWeight="300"
                  color={textPrimary}
                  lineHeight="1.5"
                  w="full"
                >
                  Because clarity creates momentum.
                </Box>
              </VStack>
            </GridItem>
          </Grid>

          <Divider borderColor={rule} opacity={0.5} my={{ base: 16, md: 24 }} />

          <Grid id="system" templateColumns={{ base: "1fr", md: "1fr 1.3fr" }} gap={{ base: 12, md: 20 }}>
            <GridItem>
              <Heading
                fontFamily="Georgia, 'Times New Roman', serif"
                fontWeight="300"
                fontSize={{ base: "2rem", md: "2.8rem" }}
                color={textPrimary}
                lineHeight="1.2"
                mt={4}
              >
                I read the system, not just the problem
              </Heading>
            </GridItem>

            <GridItem>
              <VStack align="flex-start" spacing={4}>
                <Text color={textSoft} lineHeight="1.75">
                  My work often starts by helping people see the challenge differently. I look for the patterns beneath the presenting issue, because organisational challenges rarely exist in isolation.
                </Text>
                <Text color={textSoft} lineHeight="1.75">
                  Most people focus on the message. I focus on the conditions surrounding the message and the system receiving it.
                </Text>
                <VStack
                  align="flex-start"
                  spacing={1}
                  py={4}
                  fontFamily="Georgia, 'Times New Roman', serif"
                  fontStyle="italic"
                  fontSize="1.15rem"
                  color={textPrimary}
                  lineHeight="1.6"
                >
                  <Text>Communication challenges are rarely just communication challenges.</Text>
                  <Text>Resistance is rarely just resistance.</Text>
                  <Text>Stakeholder tension is rarely just stakeholder tension.</Text>
                </VStack>
                <Text color={textSoft} lineHeight="1.75">
                  These challenges often reflect deeper organisational dynamics, competing priorities, capability gaps, unclear expectations, behavioural patterns, or structural barriers.
                </Text>
                <Text color={textSoft} lineHeight="1.75">
                  I help organisations understand the system surrounding the challenge, not simply the symptom in front of them.
                </Text>
                <Text color={textPrimary} lineHeight="1.75" fontWeight="500">
                  Because when you understand the system, better decisions become possible. And better decisions create better outcomes.
                </Text>
              </VStack>
            </GridItem>
          </Grid>
        </Container>
      </Box>

      {/* ─── MY APPROACH: SIX CONDITIONS ────────────────────────── */}
      <Box id="framework" as="section" bg={bg} py={{ base: "5rem", md: "8rem" }} px={{ base: "1.5rem", md: "5vw" }}>
        <Container maxW="container.xl" px={0}>
          <Box mb={12}>
            <Box style={labelStyle} mb={4}>
              <Box as="span" display="inline-block" w="2rem" h="1px" bg={accent} />
              My approach
            </Box>
            <Heading
              fontFamily="Georgia, 'Times New Roman', serif"
              fontWeight="300"
              fontSize={{ base: "2rem", md: "3rem" }}
              color={textPrimary}
              lineHeight="1.15"
              mt={4}
              mb={6}
            >
              The Six Conditions of Human-Centred Leadership™
            </Heading>
            <Text color={textSoft} lineHeight="1.75" maxW="65ch" mb={4}>
              Every organisation has systems, processes, strategies and structures. But lasting change ultimately depends on people.
            </Text>
            <Text color={textSoft} lineHeight="1.75" maxW="65ch" mb={4}>
              Through years of supporting organisational change, I've noticed that successful initiatives rarely succeed because of the technology, process or strategy alone. They succeed because organisations create the conditions that help people engage, adapt and move forward with confidence.
            </Text>
            <Text color={textSoft} lineHeight="1.75" maxW="65ch">
              These conditions shape how I analyse organisational challenges, design communication, support leaders and guide change initiatives.
            </Text>
          </Box>

          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing="1.5px" bg={rule}>
            {conditions.map((c) => (
              <Box
                key={c.num}
                bg={bg}
                p={{ base: 8, md: 10 }}
                position="relative"
                role="group"
                _hover={{ bg: cardBg }}
                transition="background 0.25s"
              >
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  w="full"
                  h="3px"
                  bg={accent}
                  transform="scaleX(0)"
                  transformOrigin="left"
                  transition="transform 0.35s ease"
                  _groupHover={{ transform: "scaleX(1)" }}
                />
                <Text
                  fontFamily="Georgia, 'Times New Roman', serif"
                  fontSize="3rem"
                  fontWeight="300"
                  color={isLight ? "#ede7d9" : "rgba(255,255,255,0.06)"}
                  lineHeight="1"
                  mb={6}
                >
                  {c.num}
                </Text>
                <Text
                  fontFamily="Georgia, 'Times New Roman', serif"
                  fontSize="1.1rem"
                  fontWeight="400"
                  color={textPrimary}
                  mb={3}
                  lineHeight="1.3"
                >
                  {c.title}
                </Text>
                <Text fontSize="0.88rem" color={textPrimary} lineHeight="1.6" mb={2}>
                  {c.lead}
                </Text>
                <Text fontSize="0.88rem" color={textSoft} lineHeight="1.6">
                  {c.desc}
                </Text>
              </Box>
            ))}
          </SimpleGrid>

          <Box
            mt={8}
            p={{ base: 8, md: 10 }}
            bg={cardBg}
            borderLeft={`3px solid ${accent}`}
          >
            <Text
              fontFamily="Georgia, 'Times New Roman', serif"
              fontSize={{ base: "1.4rem", md: "1.6rem" }}
              fontWeight="300"
              color={textPrimary}
              mb={4}
            >
              Why this matters
            </Text>
            <Text
              fontFamily="Georgia, 'Times New Roman', serif"
              fontSize="1.15rem"
              fontStyle="italic"
              fontWeight="300"
              color={textPrimary}
              lineHeight="1.6"
              mb={4}
            >
              Most organisations focus on what needs to be communicated. I focus on what people need in order to receive, understand and act on that communication.
            </Text>
            <Text color={textSoft} lineHeight="1.75">
              That's what allows me to support leaders and organisations through complexity, create alignment during periods of change, and design communication that works in practice, not just on paper.
            </Text>
          </Box>
        </Container>
      </Box>

      {/* ─── SERVICES ───────────────────────────────────────────── */}
      <Box id="services" as="section" bg={bgAlt} py={{ base: "5rem", md: "8rem" }} px={{ base: "1.5rem", md: "5vw" }}>
        <Container maxW="container.xl" px={0}>
          <Box mb={12}>
            <Heading
              fontFamily="Georgia, 'Times New Roman', serif"
              fontWeight="300"
              fontSize={{ base: "2rem", md: "3rem" }}
              color={textPrimary}
              mt={4}
            >
              Services
            </Heading>
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="1.5px" bg={rule}>
            {services.map((s) => (
              <Box
                key={s.num}
                bg={bg}
                p={{ base: 8, md: 12 }}
                position="relative"
                role="group"
                overflow="hidden"
                display="flex"
                flexDirection="column"
                _hover={{ bg: cardBg }}
                transition="background 0.25s"
              >
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  w="full"
                  h="3px"
                  bg={accent}
                  transform="scaleX(0)"
                  transformOrigin="left"
                  transition="transform 0.35s ease"
                  _groupHover={{ transform: "scaleX(1)" }}
                />
                <HStack justify="space-between" align="flex-start" mb={6}>
                  <Text
                    fontFamily="Georgia, 'Times New Roman', serif"
                    fontSize="3rem"
                    fontWeight="300"
                    color={isLight ? "#ede7d9" : "rgba(255,255,255,0.06)"}
                    lineHeight="1"
                  >
                    {s.num}
                  </Text>
                  <Box color={accent} opacity={0.6} transition="opacity 0.25s" _groupHover={{ opacity: 1 }}>
                    <Icon as={s.icon} boxSize="1.5rem" strokeWidth={1.25} />
                  </Box>
                </HStack>
                <Text
                  fontFamily="Georgia, 'Times New Roman', serif"
                  fontSize="1.35rem"
                  fontWeight="400"
                  color={textPrimary}
                  mb={3}
                  lineHeight="1.3"
                >
                  {s.title}
                </Text>
                <Text fontSize="0.95rem" color={textPrimary} lineHeight="1.6" mb={3}>
                  {s.lead}
                </Text>
                <Text fontSize="0.88rem" color={textSoft} lineHeight="1.6" mb={s.areas.length ? 6 : 8}>
                  {s.desc}
                </Text>

                {s.areas.length > 0 && (
                  <Box mb={8}>
                    <Text fontSize="0.7rem" fontWeight="500" letterSpacing="0.14em" textTransform="uppercase" color={accent} mb={3}>
                      Areas of support include
                    </Text>
                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacingX={6} spacingY={2}>
                      {s.areas.map((a) => (
                        <HStack key={a} spacing={3} align="center">
                          <Box as="span" display="inline-block" w="0.7rem" h="1px" bg={accent} flexShrink={0} />
                          <Text fontSize="0.85rem" color={textSoft}>{a}</Text>
                        </HStack>
                      ))}
                    </SimpleGrid>
                  </Box>
                )}

                <Box mt="auto">
                  <Button
                    variant="link"
                    color={accent}
                    fontWeight="500"
                    fontSize="0.78rem"
                    letterSpacing="0.14em"
                    textTransform="uppercase"
                    _hover={{ color: accentHover, textDecoration: "none" }}
                    onClick={() => bookConversation(s.title)}
                  >
                    Book a Conversation →
                  </Button>
                </Box>
              </Box>
            ))}
          </SimpleGrid>

          {/* CTA strip */}
          <Box
            mt="1.5px"
            bg={accent}
            px={{ base: 8, md: 12 }}
            py={{ base: 8, md: 10 }}
            cursor="pointer"
            onClick={() => bookConversation()}
            _hover={{ bg: accentHover }}
            transition="background 0.25s"
          >
            <Grid templateColumns={{ base: "1fr", md: "1fr auto" }} gap={4} alignItems="center">
              <Text
                fontFamily="Georgia, 'Times New Roman', serif"
                fontSize="1.35rem"
                color={whiteTone}
                fontStyle="italic"
                fontWeight="300"
                lineHeight="1.4"
              >
                Not sure which service fits? Let's talk.
              </Text>
              <Text fontSize="0.78rem" letterSpacing="0.14em" textTransform="uppercase" color="rgba(255,255,255,0.75)">
                Book a Conversation →
              </Text>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* ─── HOW I WORK ─────────────────────────────────────────── */}
      <Box id="process" as="section" bg={bg} py={{ base: "5rem", md: "8rem" }} px={{ base: "1.5rem", md: "5vw" }}>
        <Container maxW="container.xl" px={0}>
          <Heading
            fontFamily="Georgia, 'Times New Roman', serif"
            fontWeight="300"
            fontSize={{ base: "2rem", md: "3rem" }}
            color={textPrimary}
            lineHeight="1.15"
            mt={4}
            mb={6}
          >
            How I work
          </Heading>

          <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={0} position="relative">
            {steps.map((step, i) => (
              <Box key={step.num} p={8} pl={{ base: 8, md: i === 0 ? 0 : 8 }} role="group">
                <Box
                  w="1.1rem"
                  h="1.1rem"
                  borderRadius="full"
                  border={`2px solid ${accent}`}
                  bg={bg}
                  mb={8}
                  transition="background 0.22s"
                  _groupHover={{ bg: accent }}
                />
                <Text fontSize="0.78rem" fontWeight="400" color={accent} letterSpacing="0.12em" mb={1}>
                  Step {step.num}
                </Text>
                <Heading
                  as="h3"
                  fontFamily="Georgia, 'Times New Roman', serif"
                  fontSize="1.25rem"
                  fontWeight="400"
                  color={textPrimary}
                  mb={3}
                >
                  {step.title}
                </Heading>
                <Text fontSize="0.88rem" color={textSoft} lineHeight="1.6">
                  {step.desc}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ─── ABOUT LUNA ─────────────────────────────────────────── */}
      <Box id="about" as="section" bg={inkDark} py={{ base: "5rem", md: "8rem" }} px={{ base: "1.5rem", md: "5vw" }}>
        <Container maxW="container.xl" px={0}>
          <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={{ base: 12, md: 20 }} alignItems="start">
            {/* Portrait: replace with Luna's actual headshot when available */}
            <GridItem>
              <Box
                w="full"
                aspectRatio={{ base: "4/3", md: "3/4" } as any}
                bg="#2a221a"
                position="relative"
                overflow="hidden"
                bgGradient="linear(135deg, #2a221a 0%, #3d3020 40%, #1a140e 100%)"
              >
                {/* Dot-grid texture overlay */}
                <Box
                  position="absolute"
                  inset={0}
                  opacity={0.08}
                  style={{
                    backgroundImage: `radial-gradient(circle, ${accentLight} 1px, transparent 1px)`,
                    backgroundSize: "18px 18px",
                  }}
                />

                {/* SVG monogram composition */}
                <Box
                  position="absolute"
                  inset={0}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <svg viewBox="0 0 300 400" fill="none" xmlns="http://www.w3.org/2000/svg" width="75%" height="75%">
                    {/* Outer frame */}
                    <rect x="8" y="8" width="284" height="384" stroke={accentLight} strokeWidth="1" strokeOpacity="0.55" />
                    {/* Inner inset frame */}
                    <rect x="20" y="20" width="260" height="360" stroke={accentLight} strokeWidth="0.75" strokeOpacity="0.4" />

                    {/* Large background monogram */}
                    <text
                      x="150"
                      y="235"
                      textAnchor="middle"
                      fontFamily="Georgia, 'Times New Roman', serif"
                      fontSize="180"
                      fontWeight="300"
                      fill={accentLight}
                      fillOpacity="0.28"
                      letterSpacing="0.05em"
                    >
                      LJ
                    </text>

                    {/* Horizontal rule top */}
                    <line x1="40" y1="52" x2="260" y2="52" stroke={accentLight} strokeWidth="0.75" strokeOpacity="0.65" />
                    {/* Horizontal rule bottom */}
                    <line x1="40" y1="348" x2="260" y2="348" stroke={accentLight} strokeWidth="0.75" strokeOpacity="0.65" />

                    {/* Top-left corner mark */}
                    <polyline points="20,40 20,20 40,20" stroke={accentLight} strokeWidth="2" strokeOpacity="0.9" fill="none" />
                    {/* Top-right corner mark */}
                    <polyline points="260,20 280,20 280,40" stroke={accentLight} strokeWidth="2" strokeOpacity="0.9" fill="none" />
                    {/* Bottom-left corner mark */}
                    <polyline points="20,360 20,380 40,380" stroke={accentLight} strokeWidth="2" strokeOpacity="0.9" fill="none" />
                    {/* Bottom-right corner mark */}
                    <polyline points="260,380 280,380 280,360" stroke={accentLight} strokeWidth="2" strokeOpacity="0.9" fill="none" />

                    {/* Centre circle */}
                    <circle cx="150" cy="200" r="80" stroke={accentLight} strokeWidth="0.75" strokeOpacity="0.4" />
                    <circle cx="150" cy="200" r="50" stroke={accentLight} strokeWidth="0.5" strokeOpacity="0.3" />

                    {/* Label at base */}
                    <text
                      x="150"
                      y="370"
                      textAnchor="middle"
                      fontFamily="Georgia, 'Times New Roman', serif"
                      fontSize="9"
                      fontWeight="400"
                      fill={accentLight}
                      fillOpacity="0.8"
                      letterSpacing="0.25em"
                    >
                      L. JAMES SOLUTIONS
                    </text>
                  </svg>
                </Box>

                {/* Amber glow, bottom */}
                <Box
                  position="absolute"
                  bottom={0}
                  left={0}
                  right={0}
                  h="40%"
                  bgGradient={`linear(to-t, ${accentLight}18, transparent)`}
                  pointerEvents="none"
                />
              </Box>
            </GridItem>

            {/* About content */}
            <GridItem>
              <Box
                style={{ ...labelStyle, color: accentLight }}
                mb={4}
              >
                <Box as="span" display="inline-block" w="2rem" h="1px" bg={accentLight} />
                The person behind the practice
              </Box>
              <Heading
                fontFamily="Georgia, 'Times New Roman', serif"
                fontWeight="300"
                fontSize={{ base: "2rem", md: "2.8rem" }}
                color={whiteTone}
                lineHeight="1.2"
                mt={4}
                mb={6}
              >
                Hi, I'm Luna James
              </Heading>
              <Text color="rgba(253,250,245,0.85)" lineHeight="1.75" mb={4}>
                I'm a strategic change, communication and organisational effectiveness consultant.
              </Text>
              <Text color="rgba(253,250,245,0.7)" lineHeight="1.75" mb={4}>
                Over the past decade I've supported leaders, project teams and organisations through technology implementations, workforce transformation, organisational change, strategic communication initiatives and capability development programs.
              </Text>
              <Text color="rgba(253,250,245,0.7)" lineHeight="1.75" mb={8}>
                Throughout my career I've consistently found myself working at the intersection of people, communication, systems and change. Whether supporting a technology implementation, reviewing a strategic roadmap, designing a readiness approach or helping leaders communicate through uncertainty, I bring an ability to see connections, identify patterns and create clarity where complexity exists.
              </Text>

              <Text
                fontSize="0.8rem"
                letterSpacing="0.14em"
                textTransform="uppercase"
                color="rgba(253,250,245,0.55)"
                mb={4}
              >
                Clients often describe me as:
              </Text>
              <Wrap spacing={2} mb={8}>
                {descriptors.map((d) => (
                  <WrapItem key={d}>
                    <Tag
                      size="md"
                      bg="transparent"
                      color="rgba(253,250,245,0.75)"
                      border="1px solid rgba(201,185,154,0.3)"
                      borderRadius="none"
                      px={4}
                      py={2}
                      fontSize="0.78rem"
                      letterSpacing="0.06em"
                    >
                      {d}
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>

              <Text color="rgba(253,250,245,0.7)" lineHeight="1.75" mb={10}>
                I bring strategic thinking without unnecessary complexity and focus on solutions that people can understand, adopt and sustain.
              </Text>

              <Text
                fontFamily="Georgia, 'Times New Roman', serif"
                fontSize="1.3rem"
                fontWeight="300"
                color={whiteTone}
                mb={3}
              >
                Trusted to Deliver Change Where Clarity Matters
              </Text>
              <Text color="rgba(253,250,245,0.6)" fontSize="0.9rem" lineHeight="1.6" mb={4}>
                I support organisations through initiatives including:
              </Text>
              <VStack align="stretch" spacing={0}>
                {initiatives.map((g) => (
                  <HStack
                    key={g}
                    py={3}
                    borderBottom="1px solid rgba(201,185,154,0.12)"
                    spacing={4}
                  >
                    <Text color={accentLight} fontSize="0.78rem" flexShrink={0}>→</Text>
                    <Text fontSize="0.88rem" color="rgba(253,250,245,0.6)">{g}</Text>
                  </HStack>
                ))}
              </VStack>
            </GridItem>
          </Grid>
        </Container>
      </Box>

      {/* ─── RECENT ENGAGEMENTS ─────────────────────────────────── */}
      <Box id="engagements" as="section" bg={bg} py={{ base: "5rem", md: "8rem" }} px={{ base: "1.5rem", md: "5vw" }}>
        <Container maxW="container.xl" px={0}>
          <Box mb={12}>
            <Heading
              fontFamily="Georgia, 'Times New Roman', serif"
              fontWeight="300"
              fontSize={{ base: "2rem", md: "3rem" }}
              color={textPrimary}
              mt={4}
            >
              Recent engagements
            </Heading>
          </Box>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing="1.5px" bg={rule}>
            {engagements.map((e) => (
              <Box
                key={e.title}
                bg={bg}
                p={{ base: 8, md: 10 }}
                position="relative"
                role="group"
                _hover={{ bg: cardBg }}
                transition="background 0.25s"
              >
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  w="full"
                  h="3px"
                  bg={accent}
                  transform="scaleX(0)"
                  transformOrigin="left"
                  transition="transform 0.35s ease"
                  _groupHover={{ transform: "scaleX(1)" }}
                />
                <Text
                  fontFamily="Georgia, 'Times New Roman', serif"
                  fontSize="1.2rem"
                  fontWeight="400"
                  color={textPrimary}
                  mb={4}
                  lineHeight="1.3"
                >
                  {e.title}
                </Text>
                <Text fontSize="0.88rem" color={textSoft} lineHeight="1.7">
                  {e.desc}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ─── WHAT IT'S LIKE TO WORK WITH ME ─────────────────────── */}
      <Box id="experience" as="section" bg={bgAlt} py={{ base: "5rem", md: "8rem" }} px={{ base: "1.5rem", md: "5vw" }}>
        <Container maxW="container.xl" px={0}>
          <Grid templateColumns={{ base: "1fr", md: "1fr 1.2fr" }} gap={{ base: 12, md: 20 }}>
            <GridItem>
              <Heading
                fontFamily="Georgia, 'Times New Roman', serif"
                fontWeight="300"
                fontSize={{ base: "2rem", md: "3rem" }}
                color={textPrimary}
                lineHeight="1.15"
                mt={4}
                mb={6}
              >
                What it's like to work with me
              </Heading>
              <Text color={textSoft} lineHeight="1.75" mb={6}>
                While my work focuses on helping organisations navigate complexity and deliver change, clients often tell me the experience of working together matters just as much as the outcomes.
              </Text>
              <Text fontSize="0.7rem" fontWeight="500" letterSpacing="0.14em" textTransform="uppercase" color={accent} mb={4}>
                Common themes
              </Text>
              {bulletList(themes, textSoft, accent)}
            </GridItem>

            <GridItem>
              <VStack align="stretch" spacing={6}>
                {experienceQuotes.map((q) => (
                  <Box key={q.role} bg={cardBg} borderLeft={`3px solid ${accent}`} p={{ base: 6, md: 8 }}>
                    <Text
                      fontFamily="Georgia, 'Times New Roman', serif"
                      fontSize={{ base: "1.1rem", md: "1.2rem" }}
                      fontStyle="italic"
                      fontWeight="300"
                      color={textPrimary}
                      lineHeight="1.6"
                      mb={5}
                    >
                      “{q.quote}”
                    </Text>
                    <HStack spacing={3}>
                      <Box w="1.5rem" h="1px" bg={accent} />
                      <Text fontSize="0.75rem" letterSpacing="0.14em" textTransform="uppercase" color={textSoft}>
                        {q.role}
                      </Text>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            </GridItem>
          </Grid>
        </Container>
      </Box>

      {/* ─── TESTIMONIALS ───────────────────────────────────────── */}
      <Box
        id="testimonials"
        as="section"
        bg={inkDark}
        py={{ base: "5rem", md: "8rem" }}
        px={{ base: "1.5rem", md: "5vw" }}
      >
        <Container maxW="800px">
          <VStack spacing={0} textAlign="center">
            <Text
              fontSize="0.7rem"
              fontWeight="500"
              letterSpacing="0.18em"
              textTransform="uppercase"
              color="rgba(255,255,255,0.55)"
              mb={10}
            >
              Testimonials
            </Text>
            <Text
              fontFamily="Georgia, 'Times New Roman', serif"
              fontSize="8rem"
              lineHeight="0.5"
              color="rgba(255,255,255,0.12)"
              fontWeight="400"
              mb={8}
            >
              "
            </Text>
            <Text
              fontFamily="Georgia, 'Times New Roman', serif"
              fontSize={{ base: "1.3rem", md: "1.7rem" }}
              fontWeight="300"
              fontStyle="italic"
              color={whiteTone}
              lineHeight="1.65"
              mb={10}
              minH={{ base: "12rem", md: "9rem" }}
            >
              {testimonials[testimonialIndex].quote}
            </Text>
            <HStack spacing={4} justify="center" color="rgba(255,255,255,0.6)" mb={8}>
              <Box w="2rem" h="1px" bg="rgba(255,255,255,0.3)" />
              <Text fontSize="0.78rem" fontWeight="400" letterSpacing="0.14em" textTransform="uppercase">
                {testimonials[testimonialIndex].role}
              </Text>
              <Box w="2rem" h="1px" bg="rgba(255,255,255,0.3)" />
            </HStack>
            {/* Carousel controls */}
            <HStack spacing={6} justify="center" align="center">
              <Box
                as="button"
                onClick={() => setTestimonialIndex((i) => (i - 1 + testimonials.length) % testimonials.length)}
                color="rgba(255,255,255,0.5)"
                fontSize="1.2rem"
                lineHeight="1"
                _hover={{ color: whiteTone }}
                transition="color 0.2s"
                aria-label="Previous testimonial"
              >
                ←
              </Box>
              <HStack spacing={2}>
                {testimonials.map((_, i) => (
                  <Box
                    key={i}
                    as="button"
                    onClick={() => setTestimonialIndex(i)}
                    w={i === testimonialIndex ? "1.5rem" : "0.4rem"}
                    h="0.4rem"
                    bg={i === testimonialIndex ? whiteTone : "rgba(255,255,255,0.3)"}
                    borderRadius="full"
                    transition="all 0.25s"
                    aria-label={`Testimonial ${i + 1}`}
                  />
                ))}
              </HStack>
              <Box
                as="button"
                onClick={() => setTestimonialIndex((i) => (i + 1) % testimonials.length)}
                color="rgba(255,255,255,0.5)"
                fontSize="1.2rem"
                lineHeight="1"
                _hover={{ color: whiteTone }}
                transition="color 0.2s"
                aria-label="Next testimonial"
              >
                →
              </Box>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* ─── CLOSE CTA ──────────────────────────────────────────── */}
      <Box as="section" bg={accentHover} py={{ base: "5rem", md: "8rem" }} px={{ base: "1.5rem", md: "5vw" }}>
        <Container maxW="container.xl" px={0}>
          <Grid templateColumns={{ base: "1fr", md: "1.4fr 1fr" }} gap={{ base: 10, md: 20 }} alignItems="center">
            <GridItem>
              <Heading
                fontFamily="Georgia, 'Times New Roman', serif"
                fontWeight="300"
                fontSize={{ base: "2rem", md: "2.8rem" }}
                color={whiteTone}
                lineHeight="1.2"
                mt={4}
              >
                Need Clarity on Your Next Step?
              </Heading>
              <Text color="rgba(255,255,255,0.65)" lineHeight="1.75" mt={6}>
                Whether you're planning a transformation, implementing technology, preparing people for change, developing a roadmap, or navigating organisational complexity, I'd love to help.
              </Text>
            </GridItem>

            <GridItem>
              <Button {...primaryButtonProps} _hover={{ bg: accentLight, borderColor: accentLight }} w="full" onClick={() => bookConversation()}>
                Book a Conversation
              </Button>
            </GridItem>
          </Grid>
        </Container>
      </Box>

      {/* ─── CONTACT ────────────────────────────────────────────── */}
      <Box id="contact" as="section" bg={bgAlt} py={{ base: "5rem", md: "8rem" }} px={{ base: "1.5rem", md: "5vw" }}>
        <Container maxW="container.xl" px={0}>
          <VStack mb={12} textAlign="center" spacing={4}>
            <Box style={{ ...labelStyle, justifyContent: "center" }}>
              <Box as="span" display="inline-block" w="2rem" h="1px" bg={accent} />
              Contact
              <Box as="span" display="inline-block" w="2rem" h="1px" bg={accent} />
            </Box>
            <Heading
              fontFamily="Georgia, 'Times New Roman', serif"
              fontWeight="300"
              fontSize={{ base: "2rem", md: "3rem" }}
              color={textPrimary}
              mt={4}
            >
              Book a Conversation
            </Heading>
            <Text color={textSoft} lineHeight="1.75" maxW="45ch" textAlign="center">
              Tell me a little about what you're navigating and I'll get back to you within one business day.
            </Text>
          </VStack>

          <ContactForm
            title="Send a message"
            description="Tell me a little about what you're working on."
            submitButtonText="Send Message"
            selectedService={selectedService}
          />
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default Home;
